const FK_PREORDER_LIMIT = 'preorder_limit';

class ProductSetItemModel {
  constructor(product) {
    this.product = product;
  }
  get mustSelectNum() {
    return this.computedMustSelectNum();
  }
  get optionsLength() {
    return this.computedOptionsLength();
  }
  get hasSufficientStock() {
    return this.computedHasSufficientStock();
  }
  get hasVariations() {
    return this.computedHasVariations();
  }
  get firstCanSelectVariation() {
    return this.computedFirstCanSelectVariation();
  }
  get variationsQuantityInfo() {
    return this.computedVariationsQuantityInfo();
  }
  get variantsMap() {
    return this.computedVariationsMap();
  }
  get hasPreorderFk() {
    return window.mainConfig.merchantData.rollout_keys.includes(
      FK_PREORDER_LIMIT,
    );
  }
  get isPreorder() {
    return this.hasPreorderFk && this.product.is_preorder;
  }
  get unlimitedStock() {
    const { unlimited_quantity, out_of_stock_orderable, preorder_limit } =
      this.product;
    if (unlimited_quantity) {
      return true;
    }

    const hasPreorderFk = this.hasPreorderFk;
    if (this.isPreorder && preorder_limit === -1) {
      return true;
    }
    if (!hasPreorderFk && out_of_stock_orderable) {
      return true;
    }
    return false;
  }
  computedMustSelectNum() {
    const { min_quantity } = this.product.bundle_set;
    return min_quantity;
  }
  computedOptionsLength() {
    return this.product.field_titles?.length || 0;
  }
  computedFirstCanSelectVariation() {
    if (!this.hasVariations) {
      return null;
    }
    const { variations } = this.product;
    if (this.unlimitedStock) {
      return variations[0];
    }
    return variations.find(
      (variation) => this.computedVariationQuantityInfo(variation).max > 0,
    );
  }
  checkUnlimitedForVariation(variation) {
    if (!this.variantsMap[variation?.key]) {
      return false;
    }

    return this.unlimitedStock;
  }
  computedVariationQuantityInfo(variation) {
    const { bundle_set } = this.product;
    const { min_quantity } = bundle_set;
    const { key, quantity, preorder_limit } = variation;
    let max = min_quantity;

    if (!this.checkUnlimitedForVariation(variation)) {
      const preorderLimitValue = this.isPreorder
        ? Math.max(preorder_limit || 0, 0)
        : 0;
      const canSelectQuantity = quantity + preorderLimitValue;
      max = Math.max(Math.min(canSelectQuantity, min_quantity), 0);
    }

    const variationQuantityInfo = {
      key,
      max,
      quantity,
    };

    return variationQuantityInfo;
  }
  computedVariationsQuantityInfo() {
    const { variations } = this.product;

    return variations.reduce((current, variation) => {
      current[variation.key] = this.computedVariationQuantityInfo(variation);
      return current;
    }, {});
  }
  computedHasSufficientStock() {
    const {
      bundle_set,
      variations = [],
      quantity,
      preorder_limit,
    } = this.product;
    const { min_quantity } = bundle_set;

    if (this.unlimitedStock) {
      return true;
    }
    if (this.hasVariations) {
      const quantityTotal = variations.reduce((currentValue, variation) => {
        return currentValue + this.computedVariationQuantityInfo(variation).max;
      }, 0);
      return quantityTotal >= min_quantity;
    }

    const preorderLimitValue = this.isPreorder
      ? Math.max(preorder_limit || 0, 0)
      : 0;
    const max = quantity + preorderLimitValue;

    return max >= min_quantity;
  }
  computedHasVariations() {
    const { variations = [] } = this.product;
    return variations.length > 0;
  }
  computedVariationsMap() {
    const { variations = [] } = this.product;
    return variations.reduce((current, variation) => {
      current[variation.key] = variation;
      return current;
    }, {});
  }
  computedVariantsFieldsByVariant(selectedVariation) {
    const { field_titles = [], variant_options, variations } = this.product;
    const variation = selectedVariation || variations[0];
    const targetOptionKeys = variation.variant_option_ids;

    return field_titles.map((fieldTitle, fieldIndex) => {
      const variantOptions = variant_options.filter((option) => {
        if (option.index !== fieldIndex) {
          return false;
        }
        const currentOptionKeys = targetOptionKeys.map((key, index) => {
          if (index < fieldIndex) {
            return targetOptionKeys[index];
          }
          if (index === fieldIndex) {
            return option.key;
          }
          return undefined;
        });
        return !!this.matchVariationByOptionKeys(currentOptionKeys);
      });
      return {
        ...fieldTitle,
        variantOptions,
      };
    });
  }
  computedVariantsByOptionKey(optionKey, fieldIndex) {
    const { variations } = this.product;
    const targetVariants = variations.filter((variation) => {
      const { variant_option_ids } = variation;
      return variant_option_ids[fieldIndex] === optionKey;
    });
    return targetVariants;
  }
  checkHasStockByVariationKey(key) {
    if (this.unlimitedStock) {
      return true;
    }
    return this.computedVariationQuantityInfo(this.variantsMap[key])?.max > 0;
  }
  verifySelectedNum(num, varationKey) {
    if (!this.hasVariations) {
      if (varationKey) {
        return false;
      }
      if (this.hasSufficientStock) {
        return num <= this.mustSelectNum;
      }
      return false;
    }
    const { variationsQuantityInfo } = this;
    return varationKey
      ? num <= variationsQuantityInfo?.[varationKey]?.max
      : false;
  }
  checkHasStockByOptionKey(optionKey, fieldIndex) {
    if (this.unlimitedStock) {
      return true;
    }
    const variants = this.computedVariantsByOptionKey(optionKey, fieldIndex);
    return variants.some((variant) =>
      this.checkHasStockByVariationKey(variant?.key),
    );
  }
  matchVariationOptionKey(variant, optionKey, fieldIndex) {
    if (!variant) {
      return;
    }
    const { variant_option_ids = [] } = variant;
    return variant_option_ids[fieldIndex] === optionKey;
  }
  matchVariationByOptionKeys(optionKeys = [], allowEmptyOptionKey = true) {
    const { variations = [] } = this.product;
    const targetVariation = variations.find((variation) => {
      const { variant_option_ids } = variation;
      // NOTE: The first option will not be hidden, So the first option key must be equal
      if (variant_option_ids[0] !== optionKeys[0]) {
        return false;
      }
      const isMatch = variant_option_ids.every((key, index) => {
        if (allowEmptyOptionKey && !optionKeys[index]) {
          return true;
        }
        return key === optionKeys[index];
      });
      return isMatch;
    });
    return targetVariation || null;
  }
  matchOptionKeysLengthByVariation(variation, optionKeys = []) {
    const { variant_option_ids } = variation;
    return variant_option_ids.filter(
      (optionKey, index) => optionKey === optionKeys[index],
    ).length;
  }
  fuzzyMathVariationByOptionKeys(optionKeys = []) {
    // NOTE: The first option will not be hidden, fuzzy matching must based on the first option
    const firstOptionKey = optionKeys[0];
    const variations = this.computedVariantsByOptionKey(firstOptionKey, 0);
    const matchOptionsKeysLengths = variations.map((variation) =>
      this.matchOptionKeysLengthByVariation(variation, optionKeys),
    );
    const maxMatch = Math.max(...matchOptionsKeysLengths);
    if (maxMatch === 0) {
      return null;
    }

    const targetIndex = matchOptionsKeysLengths.indexOf(maxMatch);

    return variations[targetIndex] || null;
  }
}

export default ProductSetItemModel;
